import { action, computed, makeObservable, observable } from 'mobx'

import Localization from '../../localization/LocalizationManager'
import ProjectsStore from '../../stores/domain/Projects.store'
import ProjectDateStore, { isBetween } from '../../stores/ui/ProjectDate.store'
import {
  MAX_MONTHS_BACK,
  MAX_MONTHS_FORWARD,
} from './CommonDateFilter.constants'
import {
  CommonDateFilterMode,
  MODE_CONFIG,
  MOVEMENT_DAYS,
} from './CommonDateFilterMode'

interface ICommonDateFilters {
  startDate: Date
  daysToAdd: number
  isDateFilterActive: boolean
}

const DAYS_IN_WEEK = 7

export default class CommonDateFilterStore {
  @observable public dateFilterMode: CommonDateFilterMode =
    CommonDateFilterMode.WEEK
  @observable public isCalendarShown: boolean = false
  @observable public shouldShowModeOptions: boolean = false
  @observable public filters: ICommonDateFilters
  @observable public pendingStartDate: Date | null = null
  @observable public pendingDaysToAdd: number | null = null

  private readonly projectDateStore: ProjectDateStore
  private readonly projectsStore?: ProjectsStore
  private readonly onDateChange?: (startDate: number, endDate: number) => void
  private today = new Date()

  private get projectStartDate(): Date {
    const startTimestamp = this.projectsStore?.currentProjectStartDate
    if (startTimestamp) {
      return new Date(startTimestamp)
    }
    return this.projectDateStore.getMinAvailableDate()
  }

  private get projectEndDate(): Date {
    const endTimestamp = this.projectsStore?.currentProjectEndDate
    if (endTimestamp) {
      return new Date(endTimestamp)
    }
    return this.projectDateStore.getMaxAvailableDate()
  }

  public constructor(
    projectDateStore: ProjectDateStore,
    onDateChange?: (startDate: number, endDate: number) => void,
    initialMode: CommonDateFilterMode = CommonDateFilterMode.WEEK,
    projectsStore?: ProjectsStore,
  ) {
    this.projectDateStore = projectDateStore
    this.projectsStore = projectsStore
    this.onDateChange = onDateChange
    this.dateFilterMode = initialMode

    const today = this.projectDateStore.startOfDay(new Date())
    this.today = today
    const { startDate, daysToAdd, isDateFilterActive } = this.getInitialFilters(
      initialMode,
      today,
    )

    this.filters = observable({
      startDate,
      daysToAdd,
      isDateFilterActive,
    })

    makeObservable(this)
  }

  private getInitialFilters(
    mode: CommonDateFilterMode,
    today: Date,
  ): ICommonDateFilters {
    const { startDate, daysToAdd } = this.calculateDateRange(mode, today)
    return {
      startDate,
      daysToAdd,
      isDateFilterActive: MODE_CONFIG[mode].isDateFilterActive,
    }
  }

  private calculateDateRange(
    mode: CommonDateFilterMode,
    anchorDate: Date,
  ): { startDate: Date; daysToAdd: number } {
    const config = MODE_CONFIG[mode]
    const pds = this.projectDateStore

    switch (config.periodType) {
      case 'day':
        return { startDate: anchorDate, daysToAdd: 0 }

      case 'week': {
        const startDate = pds.startOfWeek(anchorDate)
        return { startDate, daysToAdd: DAYS_IN_WEEK * config.periodCount - 1 }
      }

      case 'month': {
        const startDate = pds.startOfMonth(anchorDate)
        const endMonth = pds.addMonths(startDate, config.periodCount - 1)
        const endDate = pds.endOfMonth(endMonth)
        return { startDate, daysToAdd: pds.countDaysToDate(startDate, endDate) }
      }

      case 'year': {
        const [year] = pds.getYearMonthDay(anchorDate)
        const startDate = pds.fromIsoString(`${year}-01-01`)
        const endDate = pds.fromIsoString(`${year}-12-31`)
        return { startDate, daysToAdd: pds.countDaysToDate(startDate, endDate) }
      }

      case 'project':
        return {
          startDate: this.projectStartDate,
          daysToAdd: pds.countDaysToDate(
            this.projectStartDate,
            this.projectEndDate,
          ),
        }
    }
  }

  @computed
  public get endDate(): Date {
    return this.projectDateStore.getEndDate(this.filters)
  }

  @computed
  public get pendingEndDate(): Date | null {
    if (this.pendingStartDate === null || this.pendingDaysToAdd === null) {
      return null
    }
    return this.projectDateStore.getEndDate({
      startDate: this.pendingStartDate,
      daysToAdd: this.pendingDaysToAdd,
    })
  }

  @computed
  public get isCurrentActive(): boolean {
    const { startDate, isDateFilterActive } = this.filters
    if (!isDateFilterActive) return false

    const today = this.projectDateStore.startOfDay(new Date())
    const { isSameDay } = this.projectDateStore

    if (isSameDay(startDate, this.endDate)) {
      return isSameDay(startDate, today)
    }

    return (
      isSameDay(startDate, today) ||
      isSameDay(this.endDate, today) ||
      isBetween(startDate, this.endDate, today)
    )
  }

  @computed
  public get canChangeDates(): boolean {
    return this.dateFilterMode !== CommonDateFilterMode.PROJECT
  }

  @computed
  public get canMoveBack(): boolean {
    return this.canStepBy(-1)
  }

  @computed
  public get canMoveForward(): boolean {
    return this.canStepBy(1)
  }

  private canStepBy(direction: 1 | -1): boolean {
    if (!this.canChangeDates) return false
    const next = this.computeNextAnchor(direction)
    return next !== null && this.isInWindow(next)
  }

  private computeNextAnchor(direction: 1 | -1): Date | null {
    const pds = this.projectDateStore
    switch (MODE_CONFIG[this.dateFilterMode].periodType) {
      case 'day':
      case 'week':
        return pds.addDays(
          this.filters.startDate,
          direction * MOVEMENT_DAYS[this.dateFilterMode],
        )
      case 'month':
        return pds.addMonths(this.filters.startDate, direction)
      case 'year':
        return pds.fromIsoString(`${this.selectedYear + direction}-01-01`)
      case 'project':
        return null
    }
  }

  private isInWindow(anchor: Date): boolean {
    const monthsFromToday = this.projectDateStore.differenceInCalendarMonths(
      anchor,
      this.today,
    )
    return (
      monthsFromToday >= -MAX_MONTHS_BACK &&
      monthsFromToday <= MAX_MONTHS_FORWARD
    )
  }

  @computed
  public get isYearPickerMode(): boolean {
    return this.dateFilterMode === CommonDateFilterMode.YEAR
  }

  @computed
  public get isProjectMode(): boolean {
    return this.dateFilterMode === CommonDateFilterMode.PROJECT
  }

  @computed
  public get selectedYear(): number {
    const [year] = this.projectDateStore.getYearMonthDay(this.filters.startDate)
    return year
  }

  @computed
  public get datesButtonCaption(): string {
    const { startDate, isDateFilterActive } = this.filters
    const {
      getDateIntervalToDisplay,
      isSameDay,
      getWeekdayMonthAndDayToDisplay,
      getMonthAndYearToDisplay,
    } = this.projectDateStore

    if (this.isProjectMode) {
      const createdAt = this.projectsStore?.activeProject?.createdAt
      if (createdAt) {
        const formatted =
          this.projectDateStore.getMonthDayAndYearToDisplay(createdAt)
        return `${formatted} - ∞`
      }
      return Localization.translator.dates
    }

    if (!startDate || !isDateFilterActive) {
      return Localization.translator.dates
    }

    if (this.dateFilterMode === CommonDateFilterMode.YEAR) {
      const [year] = this.projectDateStore.getYearMonthDay(startDate)
      return year.toString()
    }

    if (this.dateFilterMode === CommonDateFilterMode.MONTH) {
      return getMonthAndYearToDisplay(startDate)
    }

    if (this.dateFilterMode === CommonDateFilterMode.THREE_MONTHS) {
      const startMonth = getMonthAndYearToDisplay(startDate)
      const endMonth = getMonthAndYearToDisplay(this.endDate)
      if (startDate.getFullYear() === this.endDate.getFullYear()) {
        const startMonthOnly = startMonth.split(' ')[0]
        return `${startMonthOnly} - ${endMonth}`
      }
      return `${startMonth} - ${endMonth}`
    }

    if (isSameDay(startDate, this.endDate)) {
      return getWeekdayMonthAndDayToDisplay(startDate)
    }

    return getDateIntervalToDisplay(startDate, this.endDate)
  }

  @action.bound
  public setDateToToday(): void {
    const today = this.projectDateStore.startOfDay(new Date())
    this.today = today

    const { startDate, daysToAdd } = this.calculateDateRange(
      this.dateFilterMode,
      today,
    )
    this.filters.startDate = startDate
    this.filters.daysToAdd = daysToAdd
    this.filters.isDateFilterActive = true
    this.notifyDateChange()
  }

  @action.bound
  public setDateFilterMode(mode: CommonDateFilterMode): void {
    if (this.dateFilterMode === mode) {
      this.hideModeOptions()
      return
    }

    this.dateFilterMode = mode
    const today = this.projectDateStore.startOfDay(new Date())
    this.today = today

    const { startDate, daysToAdd } = this.calculateDateRange(mode, today)
    this.filters.startDate = startDate
    this.filters.daysToAdd = daysToAdd
    this.filters.isDateFilterActive = MODE_CONFIG[mode].isDateFilterActive

    this.hideModeOptions()
    this.hideCalendar()
    this.notifyDateChange()
  }

  @action.bound
  public moveDatesBack(): void {
    this.moveDates(-1)
  }

  @action.bound
  public moveDatesForward(): void {
    this.moveDates(1)
  }

  private moveDates(direction: 1 | -1): void {
    const newAnchor = this.computeNextAnchor(direction)
    if (newAnchor === null || !this.isInWindow(newAnchor)) return

    const { startDate, daysToAdd } = this.calculateDateRange(
      this.dateFilterMode,
      newAnchor,
    )
    this.filters.startDate = startDate
    this.filters.daysToAdd = daysToAdd
    this.filters.isDateFilterActive = true
    this.notifyDateChange()
  }

  @action.bound
  public toggleCalendar(): void {
    if (!this.isCalendarShown) {
      this.pendingStartDate = this.filters.startDate
      this.pendingDaysToAdd = this.filters.daysToAdd
    } else {
      this.clearPending()
    }
    this.isCalendarShown = !this.isCalendarShown
  }

  @action.bound
  public showCalendar(): void {
    this.pendingStartDate = this.filters.startDate
    this.pendingDaysToAdd = this.filters.daysToAdd
    this.isCalendarShown = true
  }

  @action.bound
  public hideCalendar(): void {
    this.clearPending()
    this.isCalendarShown = false
  }

  @action.bound
  public toggleModeOptions(): void {
    this.shouldShowModeOptions = !this.shouldShowModeOptions
  }

  @action.bound
  public hideModeOptions(): void {
    this.shouldShowModeOptions = false
  }

  @action.bound
  public handleDateSelect(date: Date): void {
    const selectedDate = this.projectDateStore.startOfDay(date)
    const { startDate, daysToAdd } = this.calculateDateRange(
      this.dateFilterMode,
      selectedDate,
    )
    this.pendingStartDate = startDate
    this.pendingDaysToAdd = daysToAdd
  }

  @action.bound
  public handleMonthSelect(date: Date): void {
    const { startDate, daysToAdd } = this.calculateDateRange(
      this.dateFilterMode,
      date,
    )
    this.pendingStartDate = startDate
    this.pendingDaysToAdd = daysToAdd
  }

  @action.bound
  public handleYearSelect(year: number): void {
    const yearStart = this.projectDateStore.fromIsoString(`${year}-01-01`)
    const { startDate, daysToAdd } = this.calculateDateRange(
      this.dateFilterMode,
      yearStart,
    )
    this.filters.startDate = startDate
    this.filters.daysToAdd = daysToAdd
    this.filters.isDateFilterActive = true
    this.notifyDateChange()
    this.hideCalendar()
  }

  @action.bound
  public applyPendingSelection(): void {
    if (this.pendingStartDate !== null && this.pendingDaysToAdd !== null) {
      this.filters.startDate = this.pendingStartDate
      this.filters.daysToAdd = this.pendingDaysToAdd
      this.filters.isDateFilterActive = true
      this.notifyDateChange()
    }
    this.clearPending()
    this.isCalendarShown = false
  }

  @action.bound
  public cancelPendingSelection(): void {
    this.clearPending()
    this.isCalendarShown = false
  }

  @action.bound
  private clearPending(): void {
    this.pendingStartDate = null
    this.pendingDaysToAdd = null
  }

  @action.bound
  public reset(): void {
    const today = this.projectDateStore.startOfDay(new Date())
    this.today = today
    this.dateFilterMode = CommonDateFilterMode.WEEK
    const { startDate, daysToAdd } = this.calculateDateRange(
      CommonDateFilterMode.WEEK,
      today,
    )
    this.filters.startDate = startDate
    this.filters.daysToAdd = daysToAdd
    this.filters.isDateFilterActive = true
    this.isCalendarShown = false
    this.shouldShowModeOptions = false
    this.notifyDateChange()
  }

  private notifyDateChange(): void {
    if (!this.onDateChange) return

    const { startOfDay, endOfDay } = this.projectDateStore

    if (!this.filters.isDateFilterActive) {
      this.onDateChange(undefined, undefined)
      return
    }

    this.onDateChange(
      startOfDay(this.filters.startDate).getTime(),
      endOfDay(this.endDate).getTime(),
    )
  }
}
